import React, { useEffect, useMemo, useState } from 'react';
import { css } from '../styled-system/css/index';
import { useCart } from '../context/CartContext';
import { Link } from 'react-router-dom';
import useStoreConfig from '../hooks/useStoreConfig';

const pageStyle = css({ maxW: '900px', m: '40px auto', p: '0 20px', color: '#111827' });
const headerStyle = css({ color: '#111827', mb: '16px' });
const tableWrapStyle = css({ w: '100%', overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' });
const tableStyle = css({ w: '100%', minW: '620px', borderCollapse: 'collapse', bg: '#fff' });
const thStyle = css({ textAlign: 'left', p: '12px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' });
const tdStyle = css({ p: '10px 12px', borderBottom: '1px dashed #e5e7eb', color: '#111827' });
const smallBtnStyle = css({ bg: 'transparent', color: '#197bbd', border: '1px solid #197bbd', p: '4px 8px', borderRadius: '6px', cursor: 'pointer', ml: '6px' });
const checkoutBarStyle = css({ mt: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' });
const primaryBtnStyle = css({ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', maxW: '100%', boxSizing: 'border-box', p: '12px 20px', lineHeight: 1.2, textAlign: 'center', textDecoration: 'none', fontWeight: 700, borderRadius: '6px', border: 'none', bg: '#197bbd', color: '#fff', cursor: 'pointer', _disabled: { opacity: 0.55, cursor: 'not-allowed' } });
const formStyle = css({ mt: '20px', p: '18px', bg: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' });
const formGridStyle = css({ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px', selectors: { '@media (max-width: 700px)': { gridTemplateColumns: '1fr' } } });
const inputStyle = css({ w: '100%', p: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', bg: '#fff', color: '#111827' });
const labelStyle = css({ display: 'grid', gap: '5px', fontSize: '0.9rem', fontWeight: 600, color: '#374151' });
const quoteStyle = css({ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', p: '10px', border: '1px solid #e5e7eb', borderRadius: '6px', mt: '8px', cursor: 'pointer' });
const bulkStyle = css({ mt: '20px', p: '18px', borderLeft: '4px solid #197bbd', bg: '#eff6ff', color: '#1f2937' });

const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC','AS','GU','MP','PR','VI'];
const formatCents = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const emptyAddress = { name: '', company: '', email: '', phone: '', line1: '', line2: '', city: '', state: '', postalCode: '' };

const Cart = () => {
  const { items, removeItem, updateQty, clearCart, totalCents } = useCart();
  const [loading, setLoading] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const { config } = useStoreConfig();
  const [error, setError] = useState('');
  const [address, setAddress] = useState(emptyAddress);
  const [quotes, setQuotes] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState('');
  const [bulkRequired, setBulkRequired] = useState(false);
  const cartKey = useMemo(() => items.map((item) => `${item.id}:${item.quantity}`).sort().join('|'), [items]);
  const totalUnits = useMemo(() => items.reduce((sum, item) => sum + item.quantity, 0), [items]);
  const exceedsUnitThreshold = config?.bulkQuoteMinUnits > 0 && totalUnits >= config.bulkQuoteMinUnits;
  const exceedsSubtotalThreshold = config?.bulkQuoteMinSubtotalCents > 0 && totalCents >= config.bulkQuoteMinSubtotalCents;
  const requiresBulkQuote = bulkRequired || exceedsUnitThreshold || exceedsSubtotalThreshold;

  useEffect(() => {
    setQuotes([]);
    setSelectedQuote('');
    setBulkRequired(false);
  }, [cartKey]);

  const cartPayload = () => items.map((item) => ({ id: item.id, quantity: item.quantity }));

  const updateAddress = (event) => {
    setAddress((current) => ({ ...current, [event.target.name]: event.target.value }));
    setQuotes([]);
    setSelectedQuote('');
  };

  const getShippingRates = async () => {
    setQuoteLoading(true);
    setError('');
    try {
      const response = await fetch('/api/shipping/quotes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: cartPayload(), address }),
      });
      const data = await response.json();
      if (!response.ok) {
        if (data.code === 'BULK_QUOTE_REQUIRED') setBulkRequired(true);
        throw new Error(data.message || 'Shipping rates are unavailable');
      }
      setQuotes(data.quotes || []);
      setSelectedQuote(data.quotes?.[0]?.token || '');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setQuoteLoading(false);
    }
  };

  const checkout = async () => {
    if (!items.length) return;
    setLoading(true);
    setError('');
    try {
      if (config?.shippingMode === 'usps' && !selectedQuote) {
        throw new Error('Get USPS rates and select a delivery option first');
      }
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: cartPayload(), shippingQuoteToken: selectedQuote || undefined }),
      });
      const data = await response.json();
      if (!response.ok) {
        if (data.code === 'BULK_QUOTE_REQUIRED') setBulkRequired(true);
        throw new Error(data.message || 'Failed to start checkout');
      }
      if (!data.url || !/^https:\/\/checkout\.stripe\.com\//.test(data.url)) throw new Error('Invalid checkout response');
      window.location.assign(data.url);
    } catch (requestError) {
      setError(requestError.message);
      setLoading(false);
    }
  };

  const selectedRate = quotes.find((quote) => quote.token === selectedQuote);
  const checkoutAvailable = config?.shippingMode !== 'usps'
    ? Boolean(config)
    : Boolean(config?.carrierQuotesConfigured);

  return (
    <main className={pageStyle}>
      <h1 className={headerStyle}>Your Cart</h1>
      {error ? <div role="alert" style={{ color: '#b91c1c', marginBottom: 12 }}>{error}</div> : null}
      {items.length === 0 ? (
        <p>Your cart is empty. <Link to="/products">Browse Products</Link></p>
      ) : (
        <>
          <div className={tableWrapStyle}>
            <table className={tableStyle}>
              <thead><tr><th className={thStyle}>Item</th><th className={thStyle}>Qty</th><th className={thStyle}>Price</th><th className={thStyle}></th></tr></thead>
              <tbody>{items.map((item) => (
                <tr key={item.id}>
                  <td className={tdStyle}>{item.name}</td>
                  <td className={tdStyle}>
                    <button aria-label={`Decrease ${item.name} quantity`} className={smallBtnStyle} onClick={() => updateQty(item.id, item.quantity - 1)}>-</button>
                    <span style={{ margin: '0 8px' }}>{item.quantity}</span>
                    <button aria-label={`Increase ${item.name} quantity`} className={smallBtnStyle} onClick={() => updateQty(item.id, item.quantity + 1)}>+</button>
                  </td>
                  <td className={tdStyle}>{formatCents(item.unitAmount * item.quantity)}</td>
                  <td className={tdStyle}><button className={smallBtnStyle} onClick={() => removeItem(item.id)}>Remove</button></td>
                </tr>
              ))}</tbody>
            </table>
          </div>

          {config && !checkoutAvailable ? (
            <section className={bulkStyle} aria-labelledby="email-order-heading">
              <h2 id="email-order-heading" style={{ marginTop: 0, fontSize: '1.15rem' }}>Online ordering is under construction</h2>
              <p style={{ marginBottom: 18, lineHeight: 1.5 }}>Online payment is not available yet. We can still take your order by email and send you a quote based on the items in this cart.</p>
              <Link className={primaryBtnStyle} to={`/contact?topic=${requiresBulkQuote ? 'bulk-order' : 'email-order'}`}>
                Email us about this order
              </Link>
            </section>
          ) : requiresBulkQuote ? (
            <section className={bulkStyle} aria-labelledby="bulk-order-heading">
              <h2 id="bulk-order-heading" style={{ marginTop: 0, fontSize: '1.15rem' }}>Bulk order quote</h2>
              <p>Large orders qualify for custom product and freight pricing. Send us this cart and your destination details for an accurate quote.</p>
              <Link className={primaryBtnStyle} to="/contact?topic=bulk-order">Request a bulk quote</Link>
            </section>
          ) : config?.shippingMode === 'usps' && checkoutAvailable ? (
            <section className={formStyle} aria-labelledby="shipping-heading">
              <h2 id="shipping-heading" style={{ marginTop: 0, fontSize: '1.15rem' }}>Shipping address</h2>
              <div className={formGridStyle}>
                <label className={labelStyle}>Full name<input className={inputStyle} name="name" autoComplete="shipping name" value={address.name} onChange={updateAddress} /></label>
                <label className={labelStyle}>Company<input className={inputStyle} name="company" autoComplete="shipping organization" value={address.company} onChange={updateAddress} /></label>
                <label className={labelStyle}>Email<input className={inputStyle} name="email" type="email" autoComplete="email" value={address.email} onChange={updateAddress} /></label>
                <label className={labelStyle}>Phone<input className={inputStyle} name="phone" type="tel" autoComplete="shipping tel" value={address.phone} onChange={updateAddress} /></label>
                <label className={labelStyle}>Address<input className={inputStyle} name="line1" autoComplete="shipping address-line1" value={address.line1} onChange={updateAddress} /></label>
                <label className={labelStyle}>Suite or unit<input className={inputStyle} name="line2" autoComplete="shipping address-line2" value={address.line2} onChange={updateAddress} /></label>
                <label className={labelStyle}>City<input className={inputStyle} name="city" autoComplete="shipping address-level2" value={address.city} onChange={updateAddress} /></label>
                <label className={labelStyle}>State<select className={inputStyle} name="state" autoComplete="shipping address-level1" value={address.state} onChange={updateAddress}><option value="">Select</option>{STATES.map((state) => <option key={state}>{state}</option>)}</select></label>
                <label className={labelStyle}>ZIP code<input className={inputStyle} name="postalCode" inputMode="numeric" autoComplete="shipping postal-code" value={address.postalCode} onChange={updateAddress} /></label>
              </div>
              <button className={primaryBtnStyle} style={{ marginTop: 14 }} disabled={quoteLoading} onClick={getShippingRates}>{quoteLoading ? 'Getting rates...' : 'Get USPS rates'}</button>
              {quotes.length ? <div style={{ marginTop: 16 }}>
                <strong>Delivery options</strong>
                {quotes.map((quote) => (
                  <label className={quoteStyle} key={quote.token}>
                    <span><input type="radio" name="shippingQuote" value={quote.token} checked={selectedQuote === quote.token} onChange={(event) => setSelectedQuote(event.target.value)} /> {quote.carrier} {quote.service}{quote.deliveryDays ? `, about ${quote.deliveryDays} business days` : ''}</span>
                    <strong>{formatCents(quote.amountCents)}</strong>
                  </label>
                ))}
              </div> : null}
            </section>
          ) : null}

          <div className={checkoutBarStyle}>
            <div><div>Items: {formatCents(totalCents)}</div>{selectedRate ? <div>Shipping &amp; handling: {formatCents(selectedRate.amountCents)}</div> : null}<strong>{selectedRate ? `Total before tax: ${formatCents(totalCents + selectedRate.amountCents)}` : `Subtotal: ${formatCents(totalCents)}`}</strong></div>
            <div><button className={smallBtnStyle} onClick={clearCart}>Clear</button>{config && !checkoutAvailable ? null : requiresBulkQuote ? <Link className={primaryBtnStyle} style={{ marginLeft: 12 }} to="/contact?topic=bulk-order">Request quote</Link> : checkoutAvailable ? <button className={primaryBtnStyle} style={{ marginLeft: 12 }} disabled={loading} onClick={checkout}>{loading ? 'Redirecting...' : 'Checkout'}</button> : <button className={primaryBtnStyle} style={{ marginLeft: 12 }} disabled>Checking checkout...</button>}</div>
          </div>
        </>
      )}
    </main>
  );
};

export default Cart;
